import type { UseMutationResult } from '@tanstack/react-query'
import { CreditCardIcon, ExternalLinkIcon, FileTextIcon, TagIcon, XIcon } from 'lucide-react'
import React from 'react'
import { toast } from 'sonner'

import { Button, ButtonLink } from 'slab'

interface PurchaseConfirmationModalProps {
  isOpen: boolean
  onClose: () => void
  merchantName: string
  redirectUri: string
  discountText: string | null
  showLmnMessage: boolean
  emitPurchaseClickedEvent: () => void
  emitMarketplaceDisclosureViewed: () => void
  handleMarketplaceDisclosureAcknowledged: () => void
  isHappiestBaby?: boolean
  happiestBabyMutation?: UseMutationResult<any, unknown, void, unknown>
}

export function PurchaseConfirmationModal ({
  isOpen,
  onClose,
  merchantName,
  redirectUri,
  discountText,
  showLmnMessage,
  emitPurchaseClickedEvent,
  emitMarketplaceDisclosureViewed,
  handleMarketplaceDisclosureAcknowledged,
  isHappiestBaby = false,
  happiestBabyMutation,
}: PurchaseConfirmationModalProps) {
  if (!isOpen) {
    return null
  }

  const modalTitleId = 'purchase-confirmation-modal-title'
  const modalDescriptionId = 'purchase-confirmation-modal-description'

  const handleContinue = () => {
    handleMarketplaceDisclosureAcknowledged()

    if (isHappiestBaby && happiestBabyMutation) {
      // For Happiest Baby, trigger the API call directly
      happiestBabyMutation.mutate(undefined, {
        onSuccess: () => {
          // On success: emit event, redirect, and close modal
          emitPurchaseClickedEvent()
          window.open(redirectUri, '_blank', 'noopener')
          onClose()
        },
        onError: () => {
          // On error: show toast and close modal
          toast.error('Failed to register with Happiest Baby')
          onClose()
        }
      })
    } else {
      // For other merchants, use existing flow
      emitPurchaseClickedEvent()
      onClose()
    }
  }

  const handleContentClick = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-gray-800/40 bg-opacity-40 p-4'
      onClick={onClose}
      role='dialog'
      aria-modal='true'
      aria-labelledby={modalTitleId}
      aria-describedby={modalDescriptionId}
    >
      <div
        className='bg-white rounded-lg shadow-xl w-full max-w-xl overflow-hidden'
        onClick={handleContentClick}
      >
        <div className='flex flex-col justify-between gap-6 pt-8 px-6 pb-6'>
          <div className='flex items-start justify-between gap-6'>
            <h3 id={modalTitleId} className='text-xl font-semibold text-gray-800'>
              We're sending you to {merchantName} to complete your purchase
            </h3>
            <XIcon className='h-6 w-6 text-gray-500 cursor-pointer' onClick={onClose} />
          </div>

          <div className='flex flex-col items-start gap-8' id={modalDescriptionId}>
            <div className='flex flex-col items-start gap-5'>
              {discountText &&
                <div className='flex items-start gap-3'>
                  <TagIcon className='h-5 w-5 text-gray-500 flex-shrink-0' />
                  <span>{discountText}</span>
                </div>}
              <div className='flex items-start gap-3'>
                <CreditCardIcon className='h-5 w-5 text-gray-500 flex-shrink-0' />
                <div className='flex flex-col gap-1 justify-center items-start'>
                  <span>Use your Thatch card to complete your purchase</span>
                  <span className='body-sm text-gray-500'>You can also use your personal card and submit your receipt to Thatch for reimbursement</span>
                </div>
              </div>
              {showLmnMessage && (
                <div className='flex items-start gap-3'>
                  <FileTextIcon className='h-5 w-5 text-gray-500 flex-shrink-0' />
                  <span>When you shop with Truemed brands, you can seamlessly qualify for a Letter of Medical Necessity (LMN)—unlocking the ability to pay with your Thatch balance.</span>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className='flex flex-col justify-center items-center gap-4 py-4 px-6 border-t border-gray-200'>
          <p className='caption text-gray-500'>
            By continuing, you confirm that you have read the&nbsp;<a href='https://thatch.com/marketplace-disclosure' target='_blank' rel='noopener noreferrer' onClick={emitMarketplaceDisclosureViewed} className='underline text-gray-500 hover:text-gray-800 whitespace-nowrap'>Marketplace Disclosure</a>
          </p>
          {isHappiestBaby
            ? (
              <Button
                onClick={handleContinue}
                disabled={happiestBabyMutation?.isPending}
                rightIcon={ExternalLinkIcon}
                size='large'
                width='full'
                variant='primary'
              >
                {happiestBabyMutation?.isPending ? 'Registering...' : `Continue to ${merchantName}`}
              </Button>
              )
            : (
              <ButtonLink href={redirectUri} target='_blank' rel='noopener' rightIcon={ExternalLinkIcon} size='large' width='full' variant='primary' onClick={handleContinue}>Continue to {merchantName}</ButtonLink>
              )}
        </div>
      </div>
    </div>
  )
}
